import {
  AppointmentDataResponse,
  CustomFields,
  GeneralConfigDTO,
} from '@utils/interfaces';
import { isEmbeddedIntegrationMode } from '@utils/enums/IntegrationModes.enum';
import { PostSubmissionError } from '@utils/enums/PostSubmissionError.enum';

import {
  CancelConfirmPageMode,
  ConfirmCancelErrorProps,
} from './CancelConfirmPage.interface';

export const getCancelConfirmRouterParams = (
  integrationMode?: string,
): URLSearchParams => {
  const routerQueryString = isEmbeddedIntegrationMode(integrationMode)
    ? globalThis.location.hash.split('?')[1] || ''
    : globalThis.location.search.substring(1);

  return new URLSearchParams(routerQueryString);
};

export const getCancelConfirmErrorParam = (): PostSubmissionError => {
  const searchParams = new URLSearchParams(
    globalThis.location.search.substring(1),
  );
  const hashParams = new URLSearchParams(
    globalThis.location.hash.split('?')[1] || '',
  );

  // OPUI can append error before the hash while embedded hash routing keeps mode in the hash.
  // That's why we search for the error param in both the search and hash params.
  return (searchParams.get('error') ??
    hashParams.get('error')) as PostSubmissionError;
};

export const isCancelConfirmAppointmentConfirmed = (
  appointmentStatus?: string,
) => appointmentStatus?.toLowerCase() === CancelConfirmPageMode.CONFIRM;

export const getCancelConfirmStatusCode = ({
  errorConf,
  errorDel,
  isCancelledError,
  isCompleteError,
  isConfirmedAppointment,
  isPastCancelError,
}: {
  errorConf?: ConfirmCancelErrorProps;
  errorDel?: ConfirmCancelErrorProps;
  isCancelledError: boolean;
  isCompleteError: boolean;
  isConfirmedAppointment: boolean;
  isPastCancelError: boolean;
}) => {
  if (isCancelledError) {
    return 'cancelled';
  }
  if (isPastCancelError) {
    return 'past';
  }
  if (isCompleteError || isConfirmedAppointment) {
    return 'confirmed';
  }

  return errorDel?.response?.data?.code || errorConf?.response?.data?.code;
};

export const getIsCancelConfirmConfirmDisabled = (
  marketingAndConsent?: CustomFields,
) => {
  if (!marketingAndConsent?.children?.length) return false;

  return marketingAndConsent.children.some(field => {
    return field.status === 'MANDATORY' && field.defaultValue === false;
  });
};

export const getHasCancelConfirmSecondaryButtonConfig = (
  generalData?: GeneralConfigDTO,
) => {
  return !!(
    generalData?.customization?.secondaryButtonLabel &&
    generalData?.customization?.secondaryButtonLink
  );
};

export const getShouldRenderCancelConfirmButtonsWrapper = (
  mode: CancelConfirmPageMode | string | null,
  hasSecondaryButtonConfig: boolean,
) => {
  if (mode === CancelConfirmPageMode.ERROR) return false;
  if (mode === CancelConfirmPageMode.CONFIRMED) {
    return hasSecondaryButtonConfig;
  }

  return true;
};

export const toCancelConfirmPatientAppointmentData = (
  data: AppointmentDataResponse,
  brandName: string | undefined,
  t: (key: string) => string,
) => ({
  appointmentType: {
    name:
      brandName && data?.idTranslation
        ? t(`appointmentTypePage.${brandName}.${data.idTranslation}.title`)
        : data.appointmentType,
    idTranslation: data?.idTranslation,
  },
  insurance: {
    type: data.insurance.type || undefined,
    id: data.insurance.id || '',
    name: data.insurance.name || undefined,
  },
  scheduleExam: {
    day: data.AppointmentExam.date,
    hour: data.AppointmentExam.startTime,
    doctor: data.AppointmentExam.doctorName,
  },
});
